import React, { useState, useEffect } from 'react';

const DOMAIN_OPTIONS = [
  { value: 'FINANCE', label: 'Finance' },
  { value: 'LEGAL', label: 'Legal' },
];

const ProfessionalsDirectory = ({ user, navigateTo, goBack, activeProfileType }) => {
  const [domain, setDomain] = useState('FINANCE');
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [selectedPro, setSelectedPro] = useState(null);

  const profileType = activeProfileType || (user?.accountType === 'personal' ? 'PERSONAL' : 'BUSINESS');

  useEffect(() => {
    fetchProfessionals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain, profileType]);

  const fetchProfessionals = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/profiles?action=listProfessionals&domain=${domain}&profileType=${profileType}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error('Failed to load professionals');
      const data = await res.json();
      setProfessionals(data.professionals || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = (pro) => {
    setSelectedPro(pro);
    navigateTo('connect-flow');
    // Pass the selected professional via sessionStorage so ConnectFlow can read it
    try {
      sessionStorage.setItem('axigon_selected_pro', JSON.stringify({ pro, domain, profileType }));
    } catch {}
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg)', paddingTop: '80px' }}>
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '40px 20px' }}>

        {/* Header */}
        <div style={{ marginBottom: '40px' }}>
          <button
            onClick={() => goBack()}
            style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: '14px', cursor: 'pointer', padding: 0, marginBottom: '16px', fontFamily: 'inherit' }}
          >
            ← Back
          </button>
          <h1 style={{ fontSize: '34px', fontWeight: 700, color: 'var(--text-primary)', margin: 0, letterSpacing: '-0.02em' }}>
            👑 Talk to an Expert
          </h1>
          <p style={{ fontSize: '17px', color: 'var(--text-secondary)', marginTop: '8px' }}>
            Connect with vetted professionals for {profileType === 'PERSONAL' ? 'personal' : 'business'} guidance.
          </p>
        </div>

        {/* Trust bar */}
        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', marginBottom: '20px', padding: '14px 18px', backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: '10px' }}>
          {[
            'All professionals verified by Axigon',
            'Typically responds within 48 hours',
            'Your data shared only with your consent',
          ].map(text => (
            <div key={text} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '13px', color: 'var(--accent)', fontWeight: 700, flexShrink: 0 }}>✓</span>
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 500 }}>{text}</span>
            </div>
          ))}
        </div>

        {/* Vetting criteria */}
        <p style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginBottom: '28px', lineHeight: 1.6, fontStyle: 'italic' }}>
          Our panel professionals are individually verified for credentials, bar registration, and client reviews before joining Axigon.
        </p>

        {/* Domain filter */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '32px' }}>
          {DOMAIN_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setDomain(opt.value)}
              style={{
                padding: '8px 20px',
                borderRadius: 'var(--radius-pill)',
                border: domain === opt.value ? '1.5px solid var(--accent)' : '1.5px solid var(--border)',
                backgroundColor: domain === opt.value ? 'var(--accent)' : 'var(--bg)',
                color: domain === opt.value ? 'var(--bg)' : 'var(--text-secondary)',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.15s',
                fontFamily: 'inherit',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* States */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-tertiary)' }}>
            Loading professionals…
          </div>
        )}

        {error && (
          <div style={{ padding: '16px', borderRadius: 'var(--radius-control)', backgroundColor: '#fff2f2', border: '1px solid #ffd6d6', color: '#c00', fontSize: '14px' }}>
            {error}
          </div>
        )}

        {!loading && !error && professionals.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <p style={{ fontSize: '15px', color: 'var(--text-secondary)', margin: '0 0 8px', fontWeight: 500 }}>We're onboarding more experts — check back soon.</p>
            <p style={{ fontSize: '13px', color: 'var(--text-tertiary)', margin: 0, lineHeight: 1.6 }}>Our team is currently verifying professionals for {domain.toLowerCase()} matters. Each professional is individually reviewed before joining.</p>
          </div>
        )}

        {/* Professional cards */}
        {!loading && !error && (
          <div style={{ display: 'grid', gap: '16px' }}>
            {professionals.map(pro => (
              <div
                key={pro.professionalId || pro._id}
                style={{
                  backgroundColor: 'var(--bg)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-card)',
                  padding: '28px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: '20px',
                }}
              >
                <div style={{ flex: 1 }}>
                  {/* Name + credential + verified badge */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '19px', fontWeight: 600, color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>
                      {pro.name}
                    </span>
                    <span style={{
                      fontSize: '11px', fontWeight: 600, color: 'var(--accent)',
                      backgroundColor: 'rgba(0,194,124,0.08)', border: '1px solid rgba(0,194,124,0.2)',
                      borderRadius: '6px', padding: '2px 8px', letterSpacing: '0.02em',
                    }}>{pro.credential}</span>
                    <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--accent)' }}>Verified ✓</span>
                  </div>

                  {/* Rating + meta row */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '10px', flexWrap: 'wrap' }}>
                    {pro.rating != null && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {[1,2,3,4,5].map(s => (
                          <span key={s} style={{ fontSize: '13px', color: s <= Math.round(pro.rating) ? '#F59E0B' : 'var(--border)', lineHeight: 1 }}>★</span>
                        ))}
                        <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-primary)', marginLeft: '3px' }}>{pro.rating}</span>
                        {pro.reviewCount > 0 && (
                          <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>({pro.reviewCount} {pro.reviewCount === 1 ? 'review' : 'reviews'})</span>
                        )}
                      </div>
                    )}
                    <span style={{
                      fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)',
                      backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-pill)', padding: '3px 10px',
                    }}>{pro.responseTime || 'Responds within 48h'}</span>
                    {pro.experience && (
                      <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>{pro.experience} exp.</span>
                    )}
                    {pro.fee && (
                      <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)' }}>{pro.fee}</span>
                    )}
                  </div>

                  {/* Bio */}
                  <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 14px' }}>
                    {pro.bio}
                  </p>

                  {/* Specialization tags */}
                  {Array.isArray(pro.specializations) && pro.specializations.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: pro.languages?.length ? '8px' : '0' }}>
                      {pro.specializations.map(spec => (
                        <span key={spec} style={{ fontSize: '12px', color: 'var(--text-secondary)', backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', padding: '3px 10px' }}>
                          {spec}
                        </span>
                      ))}
                    </div>
                  )}
                  {/* Languages */}
                  {Array.isArray(pro.languages) && pro.languages.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', fontWeight: 600 }}>Speaks:</span>
                      {pro.languages.map(lang => (
                        <span key={lang} style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>{lang}</span>
                      )).reduce((acc, el, i) => i === 0 ? [el] : [...acc, <span key={`sep-${i}`} style={{ color: 'var(--border)' }}>·</span>, el], [])}
                    </div>
                  )}
                </div>

                {/* Connect button */}
                <button
                  onClick={() => handleConnect(pro)}
                  style={{
                    flexShrink: 0,
                    padding: '10px 22px',
                    borderRadius: 'var(--radius-control)',
                    border: 'none',
                    backgroundColor: 'var(--accent)',
                    color: 'var(--bg)',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    transition: 'background-color 0.15s',
                    whiteSpace: 'nowrap',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'var(--accent-hover)'; }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'var(--accent)'; }}
                >
                  Connect
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfessionalsDirectory;
